import React, { useState, useEffect } from 'react';
import configService from '../services/configService';

const APIConfigModal = ({ isOpen, onClose, platform = null }) => {
  const [activeTab, setActiveTab] = useState(platform || 'facebook');
  const [apiKeys, setApiKeys] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [setupStep, setSetupStep] = useState(0);

  const platforms = [
    {
      key: 'facebook',
      name: 'Facebook Pages',
      icon: 'fab fa-facebook-f',
      color: '#1877F2',
      description: 'Accédez aux données de votre page Facebook',
      fields: [
        { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        { key: 'page_id', label: 'Page ID', type: 'text', required: true }
      ],
      guide: 'https://developers.facebook.com/docs/pages/access-tokens',
      quickSetup: [
        {
          step: 1,
          title: 'Créer une App Facebook',
          description: 'Allez sur Facebook Developers et créez une nouvelle application',
          action: 'Ouvrir Facebook Developers',
          link: 'https://developers.facebook.com/apps/',
          icon: 'fas fa-external-link-alt'
        },
        {
          step: 2,
          title: 'Obtenir l\'Access Token',
          description: 'Dans votre app, allez dans "Outils" > "Explorateur Graph" et générez un token',
          action: 'Ouvrir Graph Explorer',
          link: 'https://developers.facebook.com/tools/explorer/',
          icon: 'fas fa-key'
        },
        {
          step: 3,
          title: 'Trouver votre Page ID',
          description: 'Visitez votre page Facebook et copiez l\'ID depuis l\'URL ou les paramètres',
          action: 'Ouvrir ma Page Facebook',
          link: 'https://www.facebook.com/',
          icon: 'fas fa-id-card'
        }
      ]
    },
    {
      key: 'instagram',
      name: 'Instagram Business',
      icon: 'fab fa-instagram',
      color: '#E4405F',
      description: 'Analysez vos publications Instagram',
      fields: [
        { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        { key: 'business_account_id', label: 'Business Account ID', type: 'text', required: true }
      ],
      guide: 'https://developers.facebook.com/docs/instagram-api',
      quickSetup: [
        {
          step: 1,
          title: 'Connecter Instagram Business',
          description: 'Assurez-vous que votre compte Instagram est connecté à Facebook Business',
          action: 'Ouvrir Facebook Business',
          link: 'https://business.facebook.com/',
          icon: 'fas fa-link'
        },
        {
          step: 2,
          title: 'Obtenir l\'Access Token',
          description: 'Utilisez le même token que Facebook Pages (même app)',
          action: 'Utiliser le token Facebook',
          link: '#',
          icon: 'fas fa-copy'
        },
        {
          step: 3,
          title: 'Trouver l\'Instagram Business Account ID',
          description: 'Dans Facebook Business Manager, trouvez l\'ID de votre compte Instagram',
          action: 'Ouvrir Business Manager',
          link: 'https://business.facebook.com/settings/accounts',
          icon: 'fas fa-search'
        }
      ]
    },
    {
      key: 'tiktok',
      name: 'TikTok for Business',
      icon: 'fab fa-tiktok',
      color: '#000000',
      description: 'Suivez vos performances TikTok',
      fields: [
        { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        { key: 'advertiser_id', label: 'Advertiser ID', type: 'text', required: true }
      ],
      guide: 'https://ads.tiktok.com/marketing_api/docs'
    },
    {
      key: 'youtube',
      name: 'YouTube Data API',
      icon: 'fab fa-youtube',
      color: '#FF0000',
      description: 'Analysez vos chaînes YouTube',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'password', required: true },
        { key: 'channel_id', label: 'Channel ID', type: 'text', required: true }
      ],
      guide: 'https://developers.google.com/youtube/v3'
    },
    {
      key: 'google',
      name: 'Google Analytics',
      icon: 'fab fa-google',
      color: '#4285F4',
      description: 'Données d\'analyse de votre site web',
      fields: [
        { key: 'property_id', label: 'Property ID', type: 'text', required: true },
        { key: 'service_account', label: 'Service Account JSON', type: 'textarea', required: true }
      ],
      guide: 'https://developers.google.com/analytics'
    },
    {
      key: 'cloudflare',
      name: 'Cloudflare Analytics',
      icon: 'fas fa-cloud',
      color: '#F38020',
      description: 'Métriques de performance de votre site',
      fields: [
        { key: 'api_token', label: 'API Token', type: 'password', required: true },
        { key: 'zone_id', label: 'Zone ID', type: 'text', required: true }
      ],
      guide: 'https://developers.cloudflare.com/analytics'
    },
    {
      key: 'openai',
      name: 'OpenAI API',
      icon: 'fas fa-robot',
      color: '#00A67E',
      description: 'Assistant IA pour la génération de contenu',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'password', required: true }
      ],
      guide: 'https://platform.openai.com/docs',
      quickSetup: [
        {
          step: 1,
          title: 'Créer un compte OpenAI',
          description: 'Inscrivez-vous sur OpenAI Platform si vous n\'avez pas encore de compte',
          action: 'Ouvrir OpenAI Platform',
          link: 'https://platform.openai.com/',
          icon: 'fas fa-user-plus'
        },
        {
          step: 2,
          title: 'Ajouter des fonds à votre compte',
          description: 'Ajoutez des crédits à votre compte pour utiliser l\'API',
          action: 'Ouvrir la page de facturation',
          link: 'https://platform.openai.com/account/billing',
          icon: 'fas fa-credit-card'
        },
        {
          step: 3,
          title: 'Créer une clé API',
          description: 'Générez une nouvelle clé API dans la section API Keys',
          action: 'Ouvrir les clés API',
          link: 'https://platform.openai.com/api-keys',
          icon: 'fas fa-key'
        }
      ]
    },
    {
      key: 'claude',
      name: 'Claude API',
      icon: 'fas fa-brain',
      color: '#FF6B35',
      description: 'Assistant IA Claude d\'Anthropic',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'password', required: true }
      ],
      guide: 'https://docs.anthropic.com/claude/reference/getting-started-with-the-api'
    },
    {
      key: 'gemini',
      name: 'Google Gemini',
      icon: 'fab fa-google',
      color: '#4285F4',
      description: 'Assistant IA Gemini de Google',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'password', required: true }
      ],
      guide: 'https://ai.google.dev/docs'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      loadConfiguration();
    }
  }, [isOpen]);

  const loadConfiguration = async () => {
    try {
      const result = await configService.loadConfiguration();
      if (result.success) {
        setApiKeys(result.data);
      } else {
        console.error('Failed to load configuration:', result.error);
        // Fallback to empty state
        setApiKeys({});
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setApiKeys({});
    }
  };

  const handleInputChange = (platformKey, fieldKey, value) => {
    setApiKeys(prev => ({
      ...prev,
      [platformKey]: {
        ...prev[platformKey],
        [fieldKey]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      // Save each platform configuration
      const savePromises = Object.keys(apiKeys).map(async (platform) => {
        if (apiKeys[platform] && Object.values(apiKeys[platform]).some(value => value && value.trim() !== '')) {
          return await configService.saveConfiguration(platform, apiKeys[platform]);
        }
        return { success: true };
      });

      const results = await Promise.all(savePromises);
      const failedSaves = results.filter(result => !result.success);

      if (failedSaves.length === 0) {
        alert('Configuration API sauvegardée avec succès!');
        onClose();
      } else {
        console.error('Some configurations failed to save:', failedSaves);
        alert('Configuration sauvegardée avec quelques erreurs. Vérifiez la console pour plus de détails.');
        onClose();
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Erreur lors de la sauvegarde de la configuration.');
    }
  };

  const testAPI = async (platformKey) => {
    setTesting(prev => ({ ...prev, [platformKey]: true }));
    
    try {
      const credentials = apiKeys[platformKey] || {};
      
      // Test the API connection using the backend service
      const result = await configService.testPlatform(platformKey, credentials);
      
      setTestResults(prev => ({
        ...prev,
        [platformKey]: {
          success: result.success,
          message: result.success 
            ? (result.data?.message || 'Connexion réussie! API configurée correctement.')
            : (result.error || 'Échec de la connexion. Vérifiez vos clés API.')
        }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [platformKey]: {
          success: false,
          message: 'Erreur lors du test: ' + error.message
        }
      }));
    } finally {
      setTesting(prev => ({ ...prev, [platformKey]: false }));
    }
  };

  const currentPlatform = platforms.find(p => p.key === activeTab);

  const handleQuickSetup = () => {
    setShowQuickSetup(true);
    setSetupStep(0);
  };

  const nextSetupStep = () => {
    if (setupStep < currentPlatform.quickSetup.length - 1) {
      setSetupStep(setupStep + 1);
    }
  };

  const prevSetupStep = () => {
    if (setupStep > 0) {
      setSetupStep(setupStep - 1);
    }
  };

  const closeQuickSetup = () => {
    setShowQuickSetup(false);
    setSetupStep(0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copié dans le presse-papiers!');
    });
  };

  if (!isOpen) return null;

  return (
    <div className="api-config-overlay" onClick={onClose}>
      <div className="api-config-container" onClick={(e) => e.stopPropagation()}>
        <div className="api-config-header">
          <div className="api-config-title">
            <i className="fas fa-cog"></i>
            <h2>Configuration des APIs</h2>
          </div>
          <button className="api-config-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="api-config-content">
          <div className="api-config-tabs">
            {platforms.map(platform => (
              <button
                key={platform.key}
                className={`api-tab ${activeTab === platform.key ? 'active' : ''}`}
                onClick={() => setActiveTab(platform.key)}
                style={{ '--platform-color': platform.color }}
              >
                <i className={platform.icon}></i>
                <span>{platform.name}</span>
              </button>
            ))}
          </div>

          {/* Quick Setup Guide */}
          {showQuickSetup && currentPlatform.quickSetup && (
            <div className="quick-setup-overlay">
              <div className="quick-setup-container">
                <div className="quick-setup-header">
                  <h3>
                    <i className="fas fa-magic"></i>
                    Configuration Rapide - {currentPlatform.name}
                  </h3>
                  <button className="close-quick-setup" onClick={closeQuickSetup}>
                    ✕
                  </button>
                </div>
                
                <div className="quick-setup-content">
                  <div className="setup-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: `${((setupStep + 1) / currentPlatform.quickSetup.length) * 100}%` 
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      Étape {setupStep + 1} sur {currentPlatform.quickSetup.length}
                    </span>
                  </div>

                  <div className="setup-step">
                    <div className="step-icon">
                      <i className={currentPlatform.quickSetup[setupStep].icon}></i>
                    </div>
                    <div className="step-content">
                      <h4>{currentPlatform.quickSetup[setupStep].title}</h4>
                      <p>{currentPlatform.quickSetup[setupStep].description}</p>
                      
                      <div className="step-actions">
                        <a
                          href={currentPlatform.quickSetup[setupStep].link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-button primary"
                        >
                          <i className="fas fa-external-link-alt"></i>
                          {currentPlatform.quickSetup[setupStep].action}
                        </a>
                        
                        {currentPlatform.quickSetup[setupStep].link === '#' && (
                          <button
                            className="action-button secondary"
                            onClick={() => copyToClipboard('Token Facebook')}
                          >
                            <i className="fas fa-copy"></i>
                            Copier le token
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="setup-navigation">
                    <button
                      className="nav-button prev"
                      onClick={prevSetupStep}
                      disabled={setupStep === 0}
                    >
                      <i className="fas fa-arrow-left"></i>
                      Précédent
                    </button>
                    
                    {setupStep < currentPlatform.quickSetup.length - 1 ? (
                      <button
                        className="nav-button next"
                        onClick={nextSetupStep}
                      >
                        Suivant
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    ) : (
                      <button
                        className="nav-button finish"
                        onClick={closeQuickSetup}
                      >
                        <i className="fas fa-check"></i>
                        Terminer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="api-config-form">
            <div className="platform-info">
              <div className="platform-icon" style={{ color: currentPlatform.color }}>
                <i className={currentPlatform.icon}></i>
              </div>
              <div className="platform-details">
                <h3>{currentPlatform.name}</h3>
                <p>{currentPlatform.description}</p>
                <div className="platform-actions">
                  {currentPlatform.quickSetup && (
                    <button 
                      className="quick-setup-btn"
                      onClick={handleQuickSetup}
                    >
                      <i className="fas fa-magic"></i>
                      Guide de configuration rapide
                    </button>
                  )}
                  <a 
                    href={currentPlatform.guide} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="guide-link"
                  >
                    <i className="fas fa-external-link-alt"></i>
                    Documentation officielle
                  </a>
                </div>
              </div>
            </div>

            <div className="api-fields">
              {currentPlatform.fields.map(field => (
                <div key={field.key} className="api-field">
                  <label>
                    {field.label}
                    {field.required && <span className="required">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={apiKeys[activeTab]?.[field.key] || ''}
                      onChange={(e) => handleInputChange(activeTab, field.key, e.target.value)}
                      placeholder={`Entrez votre ${field.label.toLowerCase()}`}
                      rows={4}
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={apiKeys[activeTab]?.[field.key] || ''}
                      onChange={(e) => handleInputChange(activeTab, field.key, e.target.value)}
                      placeholder={`Entrez votre ${field.label.toLowerCase()}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="api-actions">
              <button
                className="test-btn"
                onClick={() => testAPI(activeTab)}
                disabled={testing[activeTab]}
              >
                {testing[activeTab] ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Test en cours...
                  </>
                ) : (
                  <>
                    <i className="fas fa-vial"></i>
                    Tester la connexion
                  </>
                )}
              </button>

              {testResults[activeTab] && (
                <div className={`test-result ${testResults[activeTab].success ? 'success' : 'error'}`}>
                  <i className={`fas ${testResults[activeTab].success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                  {testResults[activeTab].message}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="api-config-footer">
          <button className="cancel-btn" onClick={onClose}>
            Annuler
          </button>
          <button className="save-btn" onClick={handleSave}>
            <i className="fas fa-save"></i>
            Sauvegarder la configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default APIConfigModal;
